/**
 * Dashboard session management.
 *
 * Exports:
 *   - DashboardSessionContext  — React context carrying the active user
 *   - useDashboardSession()    — consumer hook for page components (reads context)
 *   - RequireDashboardAuth     — route guard; fetches session, shows spinner while
 *                                loading, redirects to /dashboard/login on auth
 *                                failure, and provides context to children
 *
 * Usage in App.tsx:
 *   <Route path="/dashboard" element={
 *     <RequireDashboardAuth><DashboardLayout /></RequireDashboardAuth>
 *   }>
 *     ...child routes...
 *   </Route>
 *
 * Usage in a page component:
 *   const { user, logout, refresh } = useDashboardSession()
 */

import { useState, useCallback, useEffect, createContext, useContext } from 'react'
import type { ReactNode, ReactElement } from 'react'
import { Navigate } from 'react-router-dom'
import { glorifyApiClient, GlorifyApiError } from '../services/api/glorify'
import type { SessionUser } from '../services/api/glorify'

// ─── Context ─────────────────────────────────────────────────────────────────

export interface DashboardSessionContextValue {
  /** The authenticated, approved user. Always non-null inside RequireDashboardAuth. */
  user: SessionUser
  /** Re-fetches the session from the server (e.g. after profile update). */
  refresh: () => Promise<void>
  /**
   * Calls POST /auth/logout, then sets user to null which causes
   * RequireDashboardAuth to auto-navigate to /dashboard/login via <Navigate>.
   * Callers do not need useNavigate.
   */
  logout: () => Promise<void>
}

// This file exports both a context and a component. React Fast Refresh only
// supports files that export components exclusively — the context and consumer
// hook are co-located here for cohesion. In practice HMR still works; only
// the module boundary won't hot-swap in isolation.
// eslint-disable-next-line react-refresh/only-export-components
export const DashboardSessionContext =
  createContext<DashboardSessionContextValue | null>(null)

/**
 * Hook for page components to read the active session.
 * Must be called inside a RequireDashboardAuth tree.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useDashboardSession(): DashboardSessionContextValue {
  const ctx = useContext(DashboardSessionContext)
  if (!ctx) {
    throw new Error('useDashboardSession must be called within RequireDashboardAuth')
  }
  return ctx
}

// ─── Internal session loader ──────────────────────────────────────────────────

interface SessionLoaderState {
  user: SessionUser | null
  isLoading: boolean
  error: GlorifyApiError | null
  refresh: () => Promise<void>
  logout: () => Promise<void>
}

function useSessionLoader(): SessionLoaderState {
  const [user, setUser] = useState<SessionUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<GlorifyApiError | null>(null)

  const refresh = useCallback(async (): Promise<void> => {
    setIsLoading(true)
    setError(null)
    try {
      const session = await glorifyApiClient.getSession()
      setUser(session)
    } catch (err) {
      if (err instanceof GlorifyApiError) {
        setError(err)
      } else {
        setError(
          new GlorifyApiError(0, err instanceof Error ? err.message : 'Network error')
        )
      }
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const logout = useCallback(async (): Promise<void> => {
    try {
      await glorifyApiClient.logout()
    } catch {
      // Swallow — server may have already invalidated the cookie; local state
      // will clear regardless, causing RequireDashboardAuth to redirect.
    }
    setUser(null)
  }, [])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh()
  }, [refresh])

  return { user, isLoading, error, refresh, logout }
}

// ─── Route guard component ───────────────────────────────────────────────────

interface RequireDashboardAuthProps {
  children: ReactNode
}

/**
 * Wrap dashboard routes with this component. While the session is loading,
 * a centered spinner is shown. On 401/403 (or any failure), the user is
 * redirected to /dashboard/login. On success, children are rendered inside
 * DashboardSessionContext.Provider so they can call useDashboardSession().
 */
export function RequireDashboardAuth({
  children,
}: RequireDashboardAuthProps): ReactElement {
  const { user, isLoading, error, refresh, logout } = useSessionLoader()

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#131318',
        }}
      >
        <div className="gd-spinner" aria-label="Loading session" />
      </div>
    )
  }

  // Redirect on any auth failure (401 / 403) or when user is null for any reason.
  if (!user || error !== null) {
    return <Navigate to="/dashboard/login" replace />
  }

  const contextValue: DashboardSessionContextValue = { user, refresh, logout }

  return (
    <DashboardSessionContext.Provider value={contextValue}>
      {children}
    </DashboardSessionContext.Provider>
  )
}
